import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import request from 'utils/request';
import './style.scss';

const ENDPOINT = 'team/groups';

export default function Team() {
  const token = useSelector((state) => state.user.token);
  const [teams, setTeams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request(ENDPOINT, token);
      if (!Array.isArray(result?.teams)) throw new Error('Invalid team list.');
      setTeams(result.teams);
      setError('');
    } catch (err) {
      setError(`Could not load teams: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);

  const mutate = async (method, team, body) => {
    setSaving(true);
    setError('');
    try {
      await request(
        team ? `${ENDPOINT}/${encodeURIComponent(team._id)}` : ENDPOINT,
        token,
        {
          method,
          body: JSON.stringify(body),
        }
      );
      setEditing(null);
      setConfirmDelete(null);
      setName('');
      await load();
    } catch (err) {
      setError(`Could not save team: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    setEditing(null);
    setName('');
    setConfirmDelete(null);
  };
  const visible = (teams || []).filter((team) =>
    team.name.toLowerCase().includes(query.trim().toLowerCase())
  );
  return (
    <div className="dashboard-list team-admin">
      <div className="team-toolbar mt-4">
        <label>
          Search teams
          <input
            className="form-control"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn-outline-dark"
          disabled={saving || loading || !teams}
          onClick={() => {
            setEditing('new');
            setName('');
            setConfirmDelete(null);
          }}
        >
          Add team
        </button>
      </div>
      {error && (
        <p role="alert">
          {error}{' '}
          <button
            type="button"
            className="btn btn-link btn-sm"
            disabled={saving}
            onClick={load}
          >
            Reload teams
          </button>
        </p>
      )}
      {loading && <p role="status">Loading teams…</p>}
      {editing === 'new' && (
        <form
          className="team-form"
          onSubmit={(event) => {
            event.preventDefault();
            mutate('POST', null, {name: name.trim()});
          }}
        >
          <label>
            Team name{' '}
            <input
              className="form-control"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              required
            />
          </label>
          <button
            type="submit"
            className="btn btn-dark"
            disabled={saving || !name.trim()}
          >
            Create
          </button>
          <button
            type="button"
            className="btn btn-link"
            disabled={saving}
            onClick={cancel}
          >
            Cancel
          </button>
        </form>
      )}
      {!loading && teams && (
        <div className="table-responsive team-dasbo-list mt-4">
          <table
            className="table table-bordered text-center mb-0"
            id="cat-table"
          >
            <thead>
              <tr className="bg-light-gray">
                <th>TEAM</th>
                <th>MEMBERS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((team) => (
                <tr key={team._id} className="bg-gray4">
                  <td>
                    {editing === team._id ? (
                      <form
                        className="team-form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          mutate('PATCH', team, {
                            name: name.trim(),
                            expectedName: team.name,
                          });
                        }}
                      >
                        <label>
                          Team name{' '}
                          <input
                            className="form-control"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            autoFocus
                            required
                          />
                        </label>
                        <button
                          type="submit"
                          className="btn btn-dark btn-sm"
                          disabled={
                            saving || !name.trim() || name.trim() === team.name
                          }
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          disabled={saving}
                          onClick={cancel}
                        >
                          Cancel
                        </button>
                      </form>
                    ) : (
                      team.name
                    )}
                  </td>
                  <td>{team.memberCount}</td>
                  <td>
                    {confirmDelete === team._id ? (
                      <div>
                        Delete {team.name}?
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          disabled={saving}
                          onClick={() =>
                            mutate('DELETE', team, {expectedName: team.name})
                          }
                        >
                          Confirm delete
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          disabled={saving}
                          onClick={cancel}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          disabled={saving}
                          onClick={() => {
                            setEditing(team._id);
                            setName(team.name);
                            setConfirmDelete(null);
                          }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          disabled={saving || team.memberCount > 0}
                          title={
                            team.memberCount > 0
                              ? 'Only empty teams can be deleted'
                              : undefined
                          }
                          onClick={() => {
                            setEditing(null);
                            setConfirmDelete(team._id);
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr>
                  <td colSpan="3">
                    {query ? 'No matching teams.' : 'No teams yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3">
        <span className="team-delete-hint">
          Only empty teams can be deleted.{' '}
        </span>
        <a href="/admin/people">Manage member accounts</a>
      </p>
    </div>
  );
}
